import React from 'react';

/**
 * Standardized loading spinner component
 * 
 * @param {Object} props
 * @param {string} props.size - 'sm' | 'md' | 'lg' | 'xl' (default: 'md')
 * @param {string} props.color - Tailwind color class (default: 'border-indigo-600')
 * @param {string} props.className - Additional CSS classes
 */
export const Spinner = ({ 
  size = 'md', 
  color = 'border-indigo-600',
  className = '' 
}) => {
  const sizeClasses = {
    sm: 'h-4 w-4',
    md: 'h-8 w-8',
    lg: 'h-12 w-12',
    xl: 'h-16 w-16'
  };

  return (
    <div 
      className={`animate-spin rounded-full border-b-2 ${color} ${sizeClasses[size]} ${className}`}
      role="status"
      aria-label="Loading"
    />
  );
};

/**
 * Full page loading overlay
 * 
 * @param {Object} props
 * @param {string} props.message - Loading message to display
 */
export const PageLoader = ({ message = 'Loading...' }) => (
  <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
    <div className="flex flex-col items-center gap-3">
      <Spinner size="md" color="border-indigo-600" />
      {message && (
        <span className="text-sm text-slate-500">{message}</span>
      )}
    </div>
  </div>
);

/**
 * Inline loading indicator for buttons or small areas
 * 
 * @param {Object} props
 * @param {string} props.text - Loading text to display
 * @param {string} props.size - Spinner size
 */
export const InlineLoader = ({ text = 'Loading...', size = 'sm' }) => (
  <span className="inline-flex items-center gap-2">
    <Spinner size={size} color="border-current" />
    {text && <span>{text}</span>}
  </span>
);

/**
 * Section/card loading state
 * 
 * @param {Object} props
 * @param {string} props.message - Loading message
 * @param {string} props.className - Additional CSS classes
 */
export const SectionLoader = ({ message = 'Loading...', className = '' }) => (
  <div className={`flex flex-col items-center justify-center py-8 ${className}`}>
    <Spinner size="lg" />
    {message && (
      <p className="mt-3 text-sm text-gray-500">{message}</p>
    )}
  </div>
);

/**
 * Skeleton loading placeholder for text
 * 
 * @param {Object} props
 * @param {string} props.width - Width class (default: 'w-full')
 * @param {string} props.height - Height class (default: 'h-4')
 * @param {string} props.className - Additional CSS classes
 */
export const SkeletonText = ({ 
  width = 'w-full', 
  height = 'h-4',
  className = '' 
}) => (
  <div 
    className={`animate-pulse bg-gray-200 rounded ${width} ${height} ${className}`}
  />
);

/**
 * Skeleton loading placeholder for circular elements (avatars)
 * 
 * @param {Object} props
 * @param {string} props.size - Size class (default: 'h-10 w-10')
 */
export const SkeletonCircle = ({ size = 'h-10 w-10' }) => (
  <div className={`animate-pulse bg-gray-200 rounded-full ${size}`} />
);

/**
 * Skeleton card placeholder
 */
export const SkeletonCard = () => (
  <div className="animate-pulse bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center gap-3 mb-4">
      <SkeletonCircle size="h-12 w-12" />
      <div className="flex-1">
        <SkeletonText width="w-1/2" className="mb-2" />
        <SkeletonText width="w-1/3" height="h-3" />
      </div>
    </div>
    <SkeletonText className="mb-2" />
    <SkeletonText width="w-3/4" />
  </div>
);

/**
 * Table row skeleton placeholder
 * 
 * @param {Object} props
 * @param {number} props.columns - Number of columns
 */
export const SkeletonTableRow = ({ columns = 4 }) => (
  <tr className="animate-pulse">
    {Array.from({ length: columns }).map((_, i) => (
      <td key={i} className="py-3 px-4">
        <SkeletonText width={i === 0 ? 'w-3/4' : 'w-1/2'} />
      </td>
    ))}
  </tr>
);

/**
 * Loading button state helper
 * 
 * @param {boolean} isLoading - Loading state
 * @param {string} loadingText - Text to show while loading
 * @param {string} defaultText - Default button text
 * @returns {React.ReactNode} Button content
 */
export const LoadingButtonContent = ({ isLoading, loadingText = 'Loading...', defaultText }) => {
  if (isLoading) {
    return <InlineLoader text={loadingText} />;
  }
  return defaultText;
};

export default {
  Spinner,
  PageLoader,
  InlineLoader,
  SectionLoader,
  SkeletonText,
  SkeletonCircle,
  SkeletonCard,
  SkeletonTableRow,
  LoadingButtonContent
};
